// ./src/components/features/PollQuestions/PollQuestionsFilterBar/PollQuestionsFilterBar.tsx
//
// Filter controls for the event PollQuestions list: by status (active / disabled),
// answer type (single / multi / free text), and poll category (from the shared "poll"
// vocabulary). Fully controlled — the page owns the value and maps it to a
// PollQuestionFilter. Question search lives in the page header, not here.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";
import { POLL_QUESTION_TYPE } from "@/utils/api";

export type PollQuestionStatusFilter = "active" | "disabled";

export interface PollQuestionsFilterValue {
  status: PollQuestionStatusFilter | null;
  questionType: string | null;
  categoryId: string | null;
}

export interface PollQuestionsFilterBarProps {
  value: PollQuestionsFilterValue;
  onChange: (value: PollQuestionsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_POLL_QUESTIONS_FILTER: PollQuestionsFilterValue = {
  status: null,
  questionType: null,
  categoryId: null,
};

export const pollQuestionsFilterActiveCount = (value: PollQuestionsFilterValue): number =>
  [value.status, value.questionType, value.categoryId].filter(Boolean).length;

const isFilterActive = (value: PollQuestionsFilterValue): boolean =>
  pollQuestionsFilterActiveCount(value) > 0;

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };
const TYPE_STYLE: React.CSSProperties = { flex: "1 1 300px", minWidth: 280 };
const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };

const ANY = "any";

const PollQuestionsFilterBarComponent: React.FC<PollQuestionsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const statusOptions = [
    { value: ANY, label: t("events:pollQuestions.filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("events:pollQuestions.filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("events:pollQuestions.filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const typeOptions = [
    { value: ANY, label: t("events:pollQuestions.filters.type.any", { defaultValue: "Any" }) },
    { value: POLL_QUESTION_TYPE.SINGLE, label: t("events:pollQuestions.types.single", { defaultValue: "Single" }) },
    { value: POLL_QUESTION_TYPE.MULTI, label: t("events:pollQuestions.types.multi", { defaultValue: "Multiple" }) },
    { value: POLL_QUESTION_TYPE.FREE_TEXT, label: t("events:pollQuestions.types.freeText", { defaultValue: "Free text" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("events:pollQuestions.filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as PollQuestionStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={TYPE_STYLE}>
        <Input.Wrapper label={t("events:pollQuestions.filters.type.label", { defaultValue: "Type" })}>
          <SegmentedControl
            data={typeOptions}
            value={value.questionType ?? ANY}
            onChange={(next) => onChange({ ...value, questionType: next === ANY ? null : next })}
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={CATEGORY_STYLE}>
        <CategorySelect
          group="poll"
          exactGroup
          label={t("events:pollQuestions.filters.category.label", { defaultValue: "Category" })}
          placeholder={t("events:pollQuestions.filters.category.placeholder", { defaultValue: "All categories" })}
          value={value.categoryId}
          onChange={(categoryId) => onChange({ ...value, categoryId })}
          disabled={disabled}
          clearable
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_POLL_QUESTIONS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const PollQuestionsFilterBar = React.memo(PollQuestionsFilterBarComponent);
